import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearBenchmarkPreview,
  fetchBenchmarkSuggestion,
  generateBenchmarkSuggestion,
  requestBenchmarkPreview,
  selectors,
  updateAllowIndividualBenchmark,
  updateSessionBenchmarkComposition,
} from 'state';
import type { BenchmarkSuggestion } from 'state';

import { useSettingsLockNudge } from '../../SessionSettingsShared/SettingsLockNudge';
import BenchmarkTypeSelector, { BenchType } from './BenchmarkTypeSelector';
import CompositionBuilderV2 from './CompositionBuilderV2';
import BenchmarkLibraryV2 from './BenchmarkLibraryV2';
import BenchmarkSuggestionV2 from './BenchmarkSuggestionV2';
import ConstantBenchmarkTile from './ConstantBenchmarkTile';
import AlphaMonitoringV2 from './AlphaMonitoringV2';
import BenchmarkPreviewV2 from './BenchmarkPreviewV2';
import RealtimeBenchmarkPreviewV2 from './RealtimeBenchmarkPreviewV2';
import {
  BasketRow,
  LibOption,
  evenSplit,
  indexByRic,
  round1,
  toLibOptions,
  validateBasket,
} from './helpers';
import './benchmark_v2.css';

type Mode = 'composition' | 'constant';

interface Props {
  historical: boolean;
  /** When true, the benchmark-editing tiles are dimmed and non-interactive
   *  (session started, no override). Preview + alpha monitoring stay live. */
  locked?: boolean;
}

/**
 * BenchmarkViewV2 — the redesigned Benchmark settings workspace.
 *
 * Self-contained and decoupled from the classic BenchmarkView, which it has
 * fully replaced as the default Benchmark settings view. It reuses the exact
 * same redux wiring as the classic editors (updateSessionBenchmarkComposition,
 * requestBenchmarkPreview,
 * updateAllowIndividualBenchmark, plus the per-sector-alpha actions inside
 * AlphaMonitoringV2), so saved settings stay fully compatible either way.
 */
const BenchmarkViewV2: React.FC<Props> = ({ historical, locked = false }) => {
  const dispatch = useDispatch();
  const session = useSelector(selectors.activeSession);

  const individual = session.allow_individual_benchmark;

  const [mode, setMode] = useState<Mode>(
    session.benchmark_constant ? 'constant' : 'composition',
  );

  // ---- Basket (shared composition) state ---------------------------------
  const libOptions: LibOption[] = useMemo(
    () => toLibOptions(session.eligible_benchmark_options),
    [session.eligible_benchmark_options],
  );
  const optionIndex = useMemo(() => indexByRic(libOptions), [libOptions]);

  const seedBasket = useCallback(
    (): BasketRow[] =>
      (session.benchmark_compositions ?? []).map(c => ({
        ric: c.ric ?? String(c.fund),
        weight: round1((c.weight ?? 0) * 100),
      })),
    [session.benchmark_compositions],
  );

  const [basket, setBasket] = useState<BasketRow[]>(seedBasket);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const savedComps = session.benchmark_compositions;
  const savedConstant = session.benchmark_constant ?? 0;
  // A constant counts as a saved benchmark too, so the preview tile shows its
  // flat line instead of the empty placeholder.
  const hasSaved = (savedComps?.length ?? 0) > 0 || savedConstant > 0;

  // Stable content signature of the saved benchmark. Many actions (e.g. toggling
  // per-sector alpha) broadcast the whole session over the socket, which hands
  // `benchmark_compositions` a fresh array reference even when its contents are
  // unchanged. Keying effects on this string instead of the reference means we
  // only re-seed / re-request the preview when the benchmark ACTUALLY changes.
  const benchmarkKey =
    (savedComps?.length ?? 0) > 0
      ? `comp:${(savedComps ?? [])
          .map(c => `${c.ric ?? c.fund}:${c.weight}`)
          .join('|')}`
      : savedConstant > 0
        ? `const:${savedConstant}`
        : 'none';

  // Re-seed whenever the persisted benchmark actually changes (after a save).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setBasket(seedBasket());
    setDirty(false);
  }, [benchmarkKey]);

  // Clear any preview when leaving the view.
  useEffect(() => () => void dispatch(clearBenchmarkPreview({})), [dispatch]);

  // Keep the permanent preview tile current with the SAVED benchmark: recompute
  // on open and whenever the persisted benchmark changes (i.e. after a save).
  // Handles both a composition basket and a constant (flat line). Historical-only
  // — the preview endpoint runs over the session's date grid.
  useEffect(() => {
    if (!historical) return;
    const comps = savedComps ?? [];
    if (comps.length > 0) {
      setPreviewLoading(true);
      dispatch(
        requestBenchmarkPreview({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          composition: comps.map(c => ({
            fund_or_asset_ric: c.ric ?? String(c.fund),
            weight: c.weight,
          })) as any,
        }),
      );
    } else if (savedConstant > 0) {
      setPreviewLoading(true);
      dispatch(requestBenchmarkPreview({ constant: savedConstant }));
    } else {
      return;
    }
    // Safety: clear the spinner even if the request fails (failReducer leaves
    // the composite empty, so the resolve-effect below wouldn't fire).
    const t = window.setTimeout(() => setPreviewLoading(false), 8000);
    return () => window.clearTimeout(t);
    // Keyed on benchmarkKey (content signature), NOT the array reference, so a
    // session self-update (e.g. alpha toggle) doesn't re-request the preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historical, benchmarkKey, dispatch]);

  // The recompute resolves once the composite series arrives.
  useEffect(() => {
    if ((session.bechmark_preview_new?.composite?.length ?? 0) > 0) {
      setPreviewLoading(false);
    }
  }, [session.bechmark_preview_new]);

  useEffect(() => {
    if (!session?.id) return;
    dispatch(fetchBenchmarkSuggestion({ session_id: session.id }));
  }, [dispatch, session?.id]);

  const selectedRics = useMemo(() => new Set(basket.map(r => r.ric)), [basket]);

  // Any basket edit dirties state (the preview tracks the saved benchmark, so
  // unsaved edits don't touch it until Save).
  const mutate = useCallback((next: BasketRow[]) => {
    setBasket(next);
    setDirty(true);
  }, []);

  const applySuggestion = useCallback(
    (suggestion: BenchmarkSuggestion) => {
      mutate(
        suggestion.components.map(component => ({
          ric: component.ric,
          weight: round1(component.weight * 100),
        })),
      );
    },
    [mutate],
  );

  const onAdd = useCallback(
    (option: LibOption) => {
      if (selectedRics.has(option.ric)) return;
      // The first constituent fills the basket at 100%; later ones start at 0.
      const weight = basket.length === 0 ? 100 : 0;
      mutate([...basket, { ric: option.ric, weight }]);
    },
    [basket, selectedRics, mutate],
  );
  const onRemove = useCallback(
    (ric: string) => mutate(basket.filter(r => r.ric !== ric)),
    [basket, mutate],
  );
  const onWeight = useCallback(
    (ric: string, weight: number | null) => {
      const w = weight ?? 0;
      // With exactly two constituents, the weights are complementary: bumping
      // one drops the other so the basket always totals 100%.
      if (basket.length === 2) {
        const other = round1(Math.max(0, Math.min(100, 100 - w)));
        mutate(basket.map(r => ({ ...r, weight: r.ric === ric ? w : other })));
        return;
      }
      mutate(basket.map(r => (r.ric === ric ? { ...r, weight: w } : r)));
    },
    [basket, mutate],
  );
  const onEvenSplit = useCallback(
    () => mutate(evenSplit(basket)),
    [basket, mutate],
  );

  const toComposition = () =>
    basket.map(r => ({ fund_or_asset_ric: r.ric, weight: r.weight / 100 }));

  const onSave = () => {
    if (!validateBasket(basket).valid) return;
    setSaving(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(
      updateSessionBenchmarkComposition({
        composition: toComposition() as any,
      }),
    );
    // The re-seed effect flips `dirty` off once the store reflects the save; the
    // saved-composition effect then auto-refreshes the preview tile.
    window.setTimeout(() => setSaving(false), 500);
  };

  // Unified benchmark type: Composition / Constant are local UI modes;
  // Individual is the redux-backed allow_individual_benchmark flag.
  const benchType: BenchType = individual ? 'individual' : mode;
  const onSelectType = (next: BenchType) => {
    if (next === 'individual') {
      if (!individual)
        dispatch(updateAllowIndividualBenchmark({ allow: true }));
      return;
    }
    if (individual) dispatch(updateAllowIndividualBenchmark({ allow: false }));
    setMode(next);
  };

  const typeSelector = (
    <BenchmarkTypeSelector value={benchType} onChange={onSelectType} />
  );

  // All three types share ONE three-column layout — switching never changes the
  // layout or hides anything, it only flips which control is active (the rest
  // grey out via .bmv2-off). Composition → basket + library; Constant → the
  // constant tile; Individual → none (each team manages their own). Alpha
  // monitoring stays active regardless of the benchmark type.
  const compositionActive = benchType === 'composition';
  const constantActive = benchType === 'constant';
  const off = (active: boolean) => (active ? '' : ' bmv2-off');
  // Session-locked (no override): dim + freeze the benchmark-editing tiles. The
  // preview and alpha-monitoring tiles are intentionally left interactive.
  const lock = locked ? ' bmv2-locked' : '';
  // Transparent catch layer per locked tile: swallows the click and pulses the
  // TopBar lock pill (same cue as the classic BenchmarkLockGate).
  const { nudge } = useSettingsLockNudge();
  const lockCatch = locked ? (
    <div className="bmv2-lock-catch" onClick={nudge} aria-hidden="true" />
  ) : null;

  const suggestionDisabledReason = useMemo(() => {
    if ((session.underlying_assets?.length ?? 0) === 0) {
      return 'Set the instrument universe first.';
    }
    if (!session.eligible_benchmark_options) {
      return session.eligible_benchmark_options_waiting
        ? 'Benchmark options are loading.'
        : 'Benchmark coverage has not finished computing.';
    }
    const candidateCount = (
      session.eligible_benchmark_options.index_funds || []
    ).filter(fund => !fund.decommissioned).length;
    if (candidateCount === 0) {
      return historical
        ? 'No benchmark-eligible index funds for this session.'
        : 'No available index funds to suggest from.';
    }
    return undefined;
  }, [
    historical,
    session.eligible_benchmark_options,
    session.eligible_benchmark_options_waiting,
    session.underlying_assets,
  ]);

  const body = (
    <div className="bmv2-comp2">
      <div className={`bmv2-comp2__options-col${lock}`}>
        {lockCatch}
        {typeSelector}
        <div className={`bmv2-comp2__suggestion${off(compositionActive)}`}>
          <BenchmarkSuggestionV2
            suggestion={session.benchmark_suggestion}
            waiting={session.benchmark_suggestion_waiting}
            disabledReason={suggestionDisabledReason}
            optionIndex={optionIndex}
            onGenerate={() =>
              dispatch(generateBenchmarkSuggestion({ session_id: session.id }))
            }
            onApply={applySuggestion}
          />
        </div>
        <div className={`bmv2-comp2__options${off(compositionActive)}`}>
          <BenchmarkLibraryV2
            options={libOptions}
            selected={selectedRics}
            waiting={session.eligible_benchmark_options_waiting}
            onAdd={onAdd}
            onRemove={onRemove}
          />
        </div>
      </div>
      <div className="bmv2-comp2__cluster">
        <div className="bmv2-comp2__mid">
          {historical ? (
            <div className="bmv2-comp2__preview">
              <BenchmarkPreviewV2
                historical={historical}
                hasSaved={hasSaved}
                loading={previewLoading}
                optionIndex={optionIndex}
                constantValue={savedConstant}
                individual={benchType === 'individual'}
              />
            </div>
          ) : compositionActive ? (
            <div className="bmv2-comp2__preview">
              <RealtimeBenchmarkPreviewV2
                sessionId={session.id}
                benchmarkKey={benchmarkKey}
                hasSaved={(savedComps?.length ?? 0) > 0 && !savedConstant}
              />
            </div>
          ) : null}
          <div
            className={`bmv2-comp2__constituents${off(compositionActive)}${lock}`}
          >
            {lockCatch}
            <CompositionBuilderV2
              basket={basket}
              optionIndex={optionIndex}
              onWeight={onWeight}
              onRemove={onRemove}
              onEvenSplit={onEvenSplit}
              onSave={onSave}
              saving={saving}
              dirty={dirty}
            />
          </div>
        </div>
        <div className="bmv2-comp2__right">
          <div className={`bmv2-rconstant${off(constantActive)}${lock}`}>
            {lockCatch}
            <ConstantBenchmarkTile />
          </div>
          {historical ? <AlphaMonitoringV2 variant="column" /> : null}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bmv2-root">
      <div className="bmv2-body">{body}</div>
    </div>
  );
};

export default BenchmarkViewV2;
